import React from 'react';
import Skeleton from 'react-loading-skeleton';
import styles from './onboarding.module.css';

// Two fixed steps every partner works through when they get started. `complete`
// is derived by the dashboard from the partner's real data; the first
// incomplete step is the "active" one and surfaces its call to action.
const STEPS = [
  {
    id: 'create_organisation',
    title: 'Create your first client organisation',
    description: 'Add a client to start managing their account and billing from one place.',
    cta_label: 'Create organisation',
    action: 'create_organisation'
  },
  {
    id: 'buy_subscription',
    title: 'Purchase your first subscription',
    description: 'Set a client up on a plan so they can start sending right away.',
    cta_label: 'Go to organisations',
    action: 'view_organisations'
  }
];

const OnboardingSkeleton = () => (
  <div className={styles.wrapper}>
    <div className={styles.header}>
      <Skeleton width={180} height={14} />
      <Skeleton height={6} borderRadius={3} style={{ marginTop: 10 }} />
    </div>
    <div className={styles.steps}>
      {STEPS.map((step) => (
        <div key={step.id} className={styles.step}>
          <Skeleton circle width={20} height={20} />
          <Skeleton width={220} height={14} />
        </div>
      ))}
    </div>
  </div>
);

export const OnboardingChecklist = ({
  has_clients = false,
  has_subscription = false,
  loading = false,
  onCreateOrganisation = () => {},
  onViewOrganisations = () => {}
}) => {
  if (loading) return <OnboardingSkeleton />;

  const completion = {
    create_organisation: has_clients,
    buy_subscription: has_subscription
  };

  const completed_count = STEPS.filter((step) => completion[step.id]).length;
  const total_count = STEPS.length;
  const progress_pct = Math.round((completed_count / total_count) * 100);
  const all_done = completed_count === total_count;

  // The active step is the first one still outstanding.
  const active_id = STEPS.find((step) => !completion[step.id])?.id;

  const runAction = (action) => {
    if (action === 'create_organisation') onCreateOrganisation();
    if (action === 'view_organisations') onViewOrganisations();
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <div className={styles.headerTop}>
          <span className={styles.title}>{all_done ? "You're all set up" : 'Get started'}</span>
          <span className={styles.count}>
            {completed_count} of {total_count} done
          </span>
        </div>
        <div className={styles.track}>
          <div className={styles.fill} style={{ width: `${progress_pct}%` }} />
        </div>
      </div>

      <div className={styles.steps}>
        {STEPS.map((step, i) => {
          const is_complete = completion[step.id];
          const is_active = step.id === active_id;
          const state = is_complete ? 'complete' : is_active ? 'active' : 'pending';

          return (
            <div key={step.id} className={`${styles.step} ${styles[state]}`}>
              <div className={styles.stepLeft}>
                <div className={styles.circle}>
                  {is_complete && <span className={styles.check}>✓</span>}
                  {is_active && <span className={styles.activeDot} />}
                </div>
                {i < STEPS.length - 1 && <div className={styles.connector} />}
              </div>
              <div className={styles.stepBody}>
                <p className={styles.stepTitle}>{step.title}</p>
                {is_active && (
                  <>
                    <p className={styles.stepDescription}>{step.description}</p>
                    <button className={styles.cta} onClick={() => runAction(step.action)}>
                      {step.cta_label}
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
